import React from 'react';

import makeStyles from '@mui/styles/makeStyles';
import { useTranslation } from 'react-i18next';
import { Box, Divider, Fab, Hidden, Typography } from '@mui/material';
import GridItem from '@jumbo/components/GridItem';
import GridContainer from '@jumbo/components/GridContainer';
import { Delete } from '@mui/icons-material';
import ThemedTooltip from '@acnb/CustomComponents/Tooltip/ThemedTooltip';

import { useLanguageReference } from 'services/Hooks/useLanguageReference';
import { getMerchandisingProgramItemSubLabel } from 'services/Utilities/Utilities';
import { useMerchandisingProgramQuoteSortedSeason } from 'services/Data/MerchandisingProgramQuoteService';

const useStyles = makeStyles(() => ({
  pageFull: {
    width: '100%',
  },
  center: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
  },
  deleteBtn: {
    position: 'absolute',
    right: '0',
    top: '0',
  },
  squareFab: {
    borderRadius: 0,
    width: 36,
    height: 36,
    minHeight: 36,
  },
}));

const MerchandisingProgramQuoteItemCard = ({ item, handleOpenDeleteItemModal }) => {
  const classes = useStyles();

  const { t } = useTranslation();
  const { labelReference } = useLanguageReference();
  const { data: sortedMerchandisingProgramSeasonEnums } = useMerchandisingProgramQuoteSortedSeason();

  return (
    <Box>
      <GridContainer>
        <GridItem xs={12}>
          <Divider />
        </GridItem>

        <GridItem xs={12} md={3}>
          <Box
            sx={{
              display: 'flex',
              alignContent: 'center',
              flexDirection: 'row',
              justifyContent: 'space-evenly',
              height: '100%',
            }}>
            <GridItem xs={12} md={3} sx={{ alignSelf: 'center' }}>
              <Box sx={{ textAlign: 'center' }}>
                <ThemedTooltip key={`item-${item.name}`} title={t('removeFromCart')} placement={'top'}>
                  <Fab
                    color="error"
                    size="small"
                    className={classes.squareFab}
                    onClick={() => {
                      handleOpenDeleteItemModal(item);
                    }}>
                    <Delete />
                  </Fab>
                </ThemedTooltip>
              </Box>
            </GridItem>
            <GridItem md={9} sx={{ alignSelf: 'center' }}></GridItem>
          </Box>
        </GridItem>

        <GridItem xs={12} md={3}>
          <Typography variant="h1">{item?.name}</Typography>
          <Typography variant="h5" color="text.secondary" sx={{ mt: 1, mb: 1 }}>
            {getMerchandisingProgramItemSubLabel(item, labelReference, sortedMerchandisingProgramSeasonEnums)}
          </Typography>

          <Divider sx={{ mt: 0, mb: item.unitPricePromotion !== item.unitPrice ? 2 : 0 }} />

          <Box
            sx={{
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'center',
              justifyContent: 'space-between',
            }}>
            <Box>
              <Box>
                <Typography variant="h2">{t('unitPrice')}</Typography>
              </Box>
            </Box>
            <Box>
              <Typography fontSize={25} color={'text.primary'} fontWeight="bold">
                ${item?.unitPrice?.toFixed(2)} {item.currency}
              </Typography>
            </Box>
          </Box>
        </GridItem>
        <Hidden mdDown>
          <GridItem xs={12} md={3} sx={{ alignSelf: 'center' }}>
            <Box fontSize={20} display="flex" justifyContent={'center'} alignItems={'center'}>
              <Box>
                <Typography variant="h2">{t('quantity')}</Typography>
              </Box>
            </Box>
            <Box display="flex" justifyContent={'center'} alignItems={'center'}>
              <Typography variant="h1">{item.quantity}</Typography>
            </Box>
          </GridItem>
          {item.quantityPerPackage > 0 && (
            <Typography fontSize={14} sx={{ mt: 1, mb: 1, fontWeight: 'bold' }}>
              {t('quantityPerPackageInfo', { quantityPerPackage: item.quantityPerPackage })}
            </Typography>
          )}
        </Hidden>

        <Hidden mdUp implementation="js">
          <GridItem xs={12}>
            <Divider />
            <Box
              xs={12}
              md={3}
              sx={{
                alignSelf: 'center',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                flexDirection: 'row',
                width: '100%',
                my: 3,
              }}>
              <Box fontSize={20} display="flex" justifyContent={'center'} alignItems={'center'}>
                <Box>
                  <Typography variant="h3">{t('quantity')}</Typography>
                </Box>
              </Box>
              <Box display="flex" justifyContent={'center'} alignItems={'center'}>
                <Typography variant="h3">{item.quantity}</Typography>
              </Box>
            </Box>
          </GridItem>
        </Hidden>
        <GridItem xs={12} md={3}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexDirection: 'column',
              height: '100%',
            }}>
            <Typography variant="h1">
              ${item?.subTotal.toFixed(2)} {item.currency}
            </Typography>
          </Box>
        </GridItem>
      </GridContainer>
    </Box>
  );
};

export default MerchandisingProgramQuoteItemCard;
